/**
 * The page is imported from **its own module**, not from the `@pages` barrel.
 *
 * The barrel re-exports every page, so a route importing it pulls all of them into one shared chunk
 * that the entry then preloads — code-splitting by route, defeated by one import.
 */
import { createFileRoute } from '@tanstack/react-router';

import { SharedI18n } from '@shared';

import { ActsPage } from '@pages/acts';
import { ActModel } from '@units/act';
import { IamService } from '@units/iam';

/**
 * `/acts` — wiring only (`rules/frontend-fsd.mdc` rule 10).
 *
 * The register of local acts and orders: what is in force, on what date, and on what basis.
 *
 * The filter lives in the address and is validated by the unit's own schema: the whole selection
 * has to survive a reload, work in back/forward and travel to a colleague as a link. An unknown
 * value falls back to the default rather than throwing — a failed `validateSearch` replaces the
 * screen with an error boundary, and with `replace: true` on every filter change the broken address
 * stays in the bar, so a reload does not help.
 */
export const Route = createFileRoute('/_authenticated/acts/')({
  beforeLoad: IamService.IamGuards.requirePermission({
    permission: 'act:read',
    // The section is in the navigation of everybody who holds the right to it, so its existence is
    // no secret inside the organization and the refusal may name what is missing. The documents
    // *inside* it are the closed contour, and answer 404 (`rules/permissions.mdc`).
    whenDenied: 'forbidden',
  }),
  validateSearch: ActModel.actRegistrySearchSchema,
  /*
   * `acts.json` (2,37 кБ gzip) приезжает своим чанком: ключи `acts.*` спрашивают только
   * `pages/acts` и `units/act`, а `@units/act` не импортирует никто, кроме двух маршрутов актов.
   * Роутер ждёт `loader` до отрисовки — экран появляется переведённым. Крошка (`nav.acts`) при этом
   * остаётся в первой загрузке: её оболочка рисует раньше, чем `loader` закончил.
   */
  loader: () => SharedI18n.loadCatalogue('acts'),
  component: ActsPage,
  staticData: { crumbKey: 'nav.acts' },
});
